import {
  ActionIcon,
  Button,
  ColorInput,
  Group,
  Paper,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { useLanguage } from "../../../../stores/LanguageStore";
import {
  IconColumns,
  IconEdit,
  IconGripHorizontal,
  IconPlus,
  IconTrash,
} from "@tabler/icons-react";
import { useState } from "react";
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { DndContext, DragOverlay } from "@dnd-kit/core";
import { createPortal } from "react-dom";
import { defaultStatuses } from "../../../../stores/PrmStore";

const ResellerLeadsSettings = ({ items, setItems, handleAdd }) => {
  const lang = useLanguage((s) => s.language);
  const [activeDrag, setActiveDrag] = useState(null);

  function handleDragStart(event) {
    setActiveDrag(items.find((x) => x.id === event.active.id));
  }

  function handleDragEnd(event) {
    const { active, over } = event;

    if (over) {
      if (active.id !== over.id) {
        setItems((items) => {
          const oldIndex = items.findIndex((x) => x.id === active.id);
          const newIndex = items.findIndex((x) => x.id === over.id);

          return arrayMove(items, oldIndex, newIndex);
        });
      }
    }
    setActiveDrag(null);
  }

  return (
    <Paper p={"md"} gap={"xl"} px={"xl"}>
      <Group>
        <IconColumns />
        <Text size="xl" fw={700}>
          {lang.resellers.create.dialog.default_statuses}
        </Text>
      </Group>

      <Group mt={16} align="start">
        {defaultStatuses().map((item, index) => {
          return (
            <Item
              key={item.id}
              index={index}
              item={item}
              setItems={setItems}
              disabled
            />
          );
        })}
        <DndContext onDragEnd={handleDragEnd} onDragStart={handleDragStart}>
          <SortableContext
            items={items}
            strategy={horizontalListSortingStrategy}
          >
            {items.map((item, index) => {
              return (
                <Item
                  key={item.id}
                  index={index}
                  item={item}
                  setItems={setItems}
                />
              );
            })}
          </SortableContext>
          {createPortal(
            <DragOverlay>
              {activeDrag ? <Item item={activeDrag} isOverlay /> : null}
            </DragOverlay>,
            document.body
          )}
        </DndContext>
        <Button
          leftSection={<IconPlus />}
          onClick={handleAdd}
          style={{ alignSelf: "center" }}
          size="md"
          variant="light"
        >
          {lang.targeting_linkedin_google.add}
        </Button>
      </Group>
    </Paper>
  );
};

const Item = ({ index, item, setItems, disabled, isOverlay }) => {
  const lang = useLanguage((s) => s.language);
  const [isEdit, setIsEdit] = useState(item?.defaultEdit || false);
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: item.id, disabled });

  const handleEdit = () => {
    const name = document.getElementById("column-name").value;
    const color = document.getElementById("column-color").value?.substring(1);

    if (!name) {
      return document.getElementById("column-name").focus();
    }
    if (!color) {
      return document.getElementById("column-color").focus();
    }

    setItems((prev) =>
      prev.map((x, i) => {
        if (i === index) {
          return { ...x, name, color, defaultEdit: false };
        }
        return x;
      })
    );

    setIsEdit(false);
  };

  const handleStopEdit = () => {
    if (item?.defaultEdit) {
      setItems((prev) =>
        prev.map((x, i) => {
          if (i === index) {
            return { ...x, defaultEdit: false };
          }
          return x;
        })
      );
    }
    setIsEdit(false);
  };

  const handleRemove = () => {
    setItems((prev) => {
      const newItems = [...prev];
      newItems.splice(index, 1);

      return newItems;
    });
  };

  return (
    <Paper
      component={Stack}
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: 10,
        border: "1px solid #00000021",
        borderColor: disabled ? "#00000021" : `#${item?.color}66`,
      }}
      py={isEdit ? 8 : 64}
      px={isEdit ? 8 : 16}
      w={disabled ? 120 : 190}
      radius={"md"}
      gap={"xs"}
      wrap="nowrap"
      shadow="sm"
      bg={disabled ? "#eee" : isEdit ? "#fff" : `#${item?.color}12`}
      pos={"relative"}
    >
      {isEdit ? (
        <>
          <TextInput
            id="column-name"
            label={lang.prm.nurturing.create_name}
            placeholder={lang.prm.status.create_name}
            required
            autoFocus
            defaultValue={item.name}
          />

          <ColorInput
            label={lang.prm.status.color}
            placeholder={lang.prm.status.color}
            required
            id="column-color"
            format="hex"
            popoverProps={{ zIndex: 1500 }}
            closeOnColorSwatchClick={false}
            withPicker
            swatches={[
              "#2e2e2e",
              "#868e96",
              "#fa5252",
              "#e64980",
              "#be4bdb",
              "#7950f2",
              "#4c6ef5",
              "#228be6",
              "#15aabf",
              "#12b886",
              "#40c057",
              "#82c91e",
              "#fab005",
              "#fd7e14",
            ]}
            disabled={isOverlay}
            defaultValue={`#${item.color}`}
          />

          <Stack mt={"md"} gap={4}>
            <Button onClick={handleEdit} variant="filled">
              {lang.global.save}
            </Button>
            <Button onClick={handleStopEdit} variant="subtle">
              {lang.global.cancel}
            </Button>
          </Stack>
        </>
      ) : (
        <>
          {!disabled && (
            <>
              <Tooltip label={lang.global.edit}>
                <ActionIcon
                  pos={"absolute"}
                  top={4}
                  left={4}
                  variant="light"
                  disabled={disabled}
                  onClick={() => setIsEdit(true)}
                  size={"lg"}
                >
                  <IconEdit size={18} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label={lang.global.delete}>
                <ActionIcon
                  pos={"absolute"}
                  top={4}
                  right={4}
                  variant="subtle"
                  disabled={disabled}
                  onClick={handleRemove}
                  color="red"
                  size={"lg"}
                >
                  <IconTrash size={18} />
                </ActionIcon>
              </Tooltip>
            </>
          )}

          <Text
            fw={600}
            size="lg"
            ta={"center"}
            c={disabled ? "dimmed" : `#${item.color}`}
          >
            {item.name}
          </Text>
        </>
      )}

      {!disabled && !isEdit && (
        <Group
          pos={"absolute"}
          bottom={0}
          left={"50%"}
          style={{
            cursor: "grab",
            opacity: 0.5,
            transform: "translateX(-50%)",
          }}
          gap={0}
          {...attributes}
          {...listeners}
        >
          <IconGripHorizontal size={18} />
          <IconGripHorizontal size={18} style={{ marginLeft: -2 }} />
          <IconGripHorizontal size={18} style={{ marginLeft: -2 }} />
        </Group>
      )}
    </Paper>
  );
};

export default ResellerLeadsSettings;
